import {
  LookupSource,
  LookupOption,
  createEntireLoadLookupSource,
} from '@/components';
import { RtkqSpin, useLazyGetRoomsListQuery } from '@/redux';
import i18next from 'i18next';
import { useMemo } from 'react';

export const WHOLE_HOME_ID = 'whole-home';
export const FLOORPLAN_ROOM_PREFIX = 'floorplan-room:';

export type RoomLookupValue =
  | typeof WHOLE_HOME_ID
  | `${typeof FLOORPLAN_ROOM_PREFIX}${string}`;

export function toFloorplanRoomValue(floorplanRoomId: string): RoomLookupValue {
  return `${FLOORPLAN_ROOM_PREFIX}${floorplanRoomId}`;
}

export function isRoomLookupValue(value: string): value is RoomLookupValue {
  return value === WHOLE_HOME_ID || value.startsWith(FLOORPLAN_ROOM_PREFIX);
}

export function parseRoomLookupValue(value: RoomLookupValue) {
  if (value === WHOLE_HOME_ID) {
    return {
      type: 'wholeHome' as const,
    };
  }

  return {
    type: 'floorplanRoom' as const,
    floorplanRoomId: value.slice(FLOORPLAN_ROOM_PREFIX.length),
  };
}

function getWholeHomeLookupOption(): LookupOption {
  return {
    label: i18next.t<string>('rooms.wholeHome'),
    value: WHOLE_HOME_ID,
  };
}

export function mapRoomLookup(value: {
  id: string;
  name: string;
}): LookupOption {
  return {
    label: value.name,
    value: toFloorplanRoomValue(value.id),
  };
}

interface Props {
  communityId?: string;
  floorplanId?: string;
  homeId?: string;
  withWholeHome?: boolean;
}

export function useRoomLookupSource(props: Props): LookupSource {
  const { communityId, floorplanId, homeId, withWholeHome } = props;

  const [fetchList] = useLazyGetRoomsListQuery();

  return useMemo(() => {
    return createEntireLoadLookupSource({
      initial: (value: string) => {
        if (!isRoomLookupValue(value)) {
          return Promise.resolve(null!);
        }

        const parsed = parseRoomLookupValue(value);

        if (parsed.type === 'wholeHome') {
          return Promise.resolve(getWholeHomeLookupOption());
        }

        return fetchList(
          { communityId, floorplanId, homeId, [RtkqSpin]: false },
          true,
        )
          .unwrap()
          .then((values) => {
            const room = values.find((x) => x.id === parsed.floorplanRoomId);

            return room ? mapRoomLookup(room) : null!;
          });
      },

      load: () =>
        fetchList({ communityId, floorplanId, homeId, [RtkqSpin]: false })
          .unwrap()
          .then((values) => {
            const options = values.map(mapRoomLookup);

            return withWholeHome
              ? [getWholeHomeLookupOption(), ...options]
              : options;
          }),
    });
  }, [communityId, fetchList, floorplanId, homeId, withWholeHome]);
}
